import type { Theme } from '@aws-amplify/ui-react';
import {
  BRAND_PRIMARY_DARK,
  BRAND_PRIMARY_LIGHT,
  BRAND_PRIMARY_MAIN,
  FONT_FAMILY,
} from './tokens';

const amplifyBrandPrimary = {
  10: '#f3f1fc',
  20: '#e5e1f9',
  40: BRAND_PRIMARY_LIGHT,
  60: BRAND_PRIMARY_MAIN,
  80: BRAND_PRIMARY_DARK,
  90: '#5c52c4',
  100: '#453da8',
};

const sharedFieldControl = {
  borderRadius: { value: '8px' },
  fontSize: { value: '0.875rem' },
  paddingBlockStart: { value: '0.625rem' },
  paddingBlockEnd: { value: '0.625rem' },
  paddingInlineStart: { value: '0.875rem' },
  paddingInlineEnd: { value: '0.875rem' },
};

const sharedComponents = {
  button: {
    fontWeight: { value: '500' },
    primary: {
      backgroundColor: { value: BRAND_PRIMARY_MAIN },
      color: { value: '#ffffff' },
      borderRadius: { value: '8px' },
      _hover: { backgroundColor: { value: BRAND_PRIMARY_DARK } },
      _focus: { backgroundColor: { value: BRAND_PRIMARY_DARK } },
      _active: { backgroundColor: { value: '#5c52c4' } },
    },
    link: {
      color: { value: BRAND_PRIMARY_MAIN },
      _hover: { color: { value: BRAND_PRIMARY_DARK } },
      _focus: { color: { value: BRAND_PRIMARY_DARK } },
    },
  },
  fieldcontrol: sharedFieldControl,
  tabs: {
    item: {
      _active: {
        color: { value: BRAND_PRIMARY_MAIN },
        borderColor: { value: BRAND_PRIMARY_MAIN },
      },
    },
  },
};

export const amplifyLightTheme: Theme = {
  name: 'cnapse-insight-light-theme',
  tokens: {
    fonts: {
      default: {
        variable: { value: FONT_FAMILY },
        static: { value: FONT_FAMILY },
      },
    },
    colors: {
      background: {
        primary: { value: '#f5f5f5' },
        secondary: { value: '#ffffff' },
      },
      font: {
        primary: { value: '#1a1a1a' },
        secondary: { value: 'rgba(0, 0, 0, 0.6)' },
        interactive: { value: BRAND_PRIMARY_MAIN },
      },
      border: {
        primary: { value: 'rgba(0, 0, 0, 0.12)' },
        focus: { value: BRAND_PRIMARY_MAIN },
      },
      brand: {
        primary: amplifyBrandPrimary,
      },
    },
    radii: {
      small: { value: '8px' },
      medium: { value: '8px' },
      large: { value: '12px' },
    },
    components: sharedComponents,
  },
};

export const amplifyDarkTheme: Theme = {
  name: 'cnapse-insight-dark-theme',
  tokens: {
    fonts: {
      default: {
        variable: { value: FONT_FAMILY },
        static: { value: FONT_FAMILY },
      },
    },
    colors: {
      background: {
        primary: { value: '#121212' },
        secondary: { value: '#2d2d2d' },
      },
      font: {
        primary: { value: '#ffffff' },
        secondary: { value: 'rgba(255, 255, 255, 0.7)' },
        interactive: { value: BRAND_PRIMARY_LIGHT },
      },
      border: {
        primary: { value: 'rgba(255, 255, 255, 0.12)' },
        focus: { value: BRAND_PRIMARY_MAIN },
      },
      brand: {
        primary: amplifyBrandPrimary,
      },
    },
    radii: {
      small: { value: '8px' },
      medium: { value: '8px' },
      large: { value: '12px' },
    },
    components: {
      ...sharedComponents,
      button: {
        ...sharedComponents.button,
        primary: {
          ...sharedComponents.button.primary,
          _hover: { backgroundColor: { value: BRAND_PRIMARY_LIGHT } },
          _focus: { backgroundColor: { value: BRAND_PRIMARY_LIGHT } },
        },
        link: {
          color: { value: BRAND_PRIMARY_LIGHT },
          _hover: { color: { value: BRAND_PRIMARY_MAIN } },
          _focus: { color: { value: BRAND_PRIMARY_MAIN } },
        },
      },
      tabs: {
        item: {
          color: { value: 'rgba(255, 255, 255, 0.7)' },
          _active: {
            color: { value: BRAND_PRIMARY_LIGHT },
            borderColor: { value: BRAND_PRIMARY_LIGHT },
          },
        },
      },
    },
  },
};

export function createAmplifyTheme(mode: 'light' | 'dark'): Theme {
  return mode === 'dark' ? amplifyDarkTheme : amplifyLightTheme;
}
